import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ShieldCheck, Upload, RotateCcw, Trash2, Search, Copy, Check, RefreshCw, Download, History, ChevronRight, ChevronDown } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Textarea,
  Badge,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tooltip,
  TooltipProvider,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useCerts,
  useIssueCert,
  useImportCert,
  useRenewCert,
  useRevokeCert,
  useRecheckCert,
  useCertBundle,
  useActivityInfinite,
  type Cert,
  type CertStatus,
  type Bundle,
  type Activity,
} from '@/api/tiger';
import { trackFeatureAction } from '@/lib/analytics-events';
import { useDebounce } from '../shared';

type Tone = 'success' | 'danger' | 'warning' | 'muted' | 'info';

const STATUS_OPTIONS: { value: CertStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'awaiting_dns', label: 'Awaiting DNS' },
  { value: 'issuing', label: 'Issuing' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'revoked', label: 'Revoked' },
];

/** Statuses that resolve on their own — poll the list while any are present. */
const TRANSIENT: ReadonlySet<string> = new Set(['awaiting_dns', 'issuing', 'pending']);

/** Humanise the wire status for display (e.g. awaiting_dns → "awaiting DNS"). */
function statusLabel(s: string): string {
  return s === 'awaiting_dns' ? 'awaiting DNS' : s;
}

function statusTone(s: string): Tone {
  if (s === 'active') return 'success';
  if (s === 'awaiting_dns') return 'info';
  if (s === 'issuing' || s === 'pending') return 'warning';
  if (s === 'failed' || s === 'revoked' || s === 'revoking') return 'danger';
  return 'muted';
}

function sourceLabel(s: string): string {
  return s === 'internal_le' ? "Let's Encrypt" : s === 'imported' ? 'Uploaded' : s;
}

const mono = { fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)' } as const;

/** ISO date → `DD/MM/YYYY`; "—" when absent/invalid. */
function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Expiry warning badge from an ISO expiry. */
function expiryBadge(iso?: string) {
  if (!iso) return null;
  const days = (new Date(iso).getTime() - Date.now()) / 86_400_000;
  if (Number.isNaN(days)) return null;
  if (days < 0) return <Badge tone="danger" appearance="subtle" size="sm">Expired</Badge>;
  if (days < 30) return <Badge tone="warning" appearance="subtle" size="sm">Expiring soon</Badge>;
  return null;
}

/** Whole days from now until an ISO date; null when absent/invalid. */
function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((d - Date.now()) / 86_400_000);
}

/** cert-manager auto-renews ~30 days out; manual renew is gated to this window. */
const RENEW_WINDOW_DAYS = 30;

/** Parse a free-text domain list (comma/space/newline separated) into SANs. */
function parseDomains(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((d) => d.trim())
    .filter(Boolean);
}

/** Filesystem-safe base name from a domain (wildcard `*.` → `wildcard.`). */
function safeFileBase(domain: string): string {
  return domain.replace(/^\*\./, 'wildcard.').replace(/[^a-zA-Z0-9._-]/g, '_');
}

/** Trigger a client-side download of text content as a file. */
function triggerDownload(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function CertInventoryPage() {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const [status, setStatus] = useState<CertStatus | 'all'>('all');

  const { data: certs = [], isLoading, isError, refetch, isFetching } = useCerts({
    domain: search || undefined,
    status: status === 'all' ? undefined : status,
    limit: 200,
  });

  // Poll while any cert is still resolving (awaiting_dns / issuing / pending)
  // so an awaiting_dns → active transition (or a recheck) reflects on its own.
  const hasTransient = useMemo(() => certs.some((c) => TRANSIENT.has(c.status)), [certs]);
  useEffect(() => {
    if (!hasTransient) return;
    const t = setInterval(() => refetch(), 8000);
    return () => clearInterval(t);
  }, [hasTransient, refetch]);

  // External-domain certs waiting for their delegation CNAME — surfaced up top.
  const awaitingDelegation = useMemo(
    () => certs.filter((c) => c.status === 'awaiting_dns' && c.delegation),
    [certs],
  );

  const columns = useMemo<ColumnDef<Cert, unknown>[]>(
    () => [
      {
        id: 'domains',
        header: 'Domains',
        cell: ({ row }) => (
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
            {row.original.dns_names?.join(', ') || '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.status)}>{statusLabel(row.original.status)}</StatusPill>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        cell: ({ row }) => (
          <Badge tone="neutral" appearance="subtle" size="sm">
            {sourceLabel(row.original.source)}
          </Badge>
        ),
      },
      {
        id: 'expiry',
        header: 'Expires',
        cell: ({ row }) => {
          const exp = row.original.current_revision?.expires_at;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {fmtDate(exp)}
              {expiryBadge(exp)}
            </span>
          );
        },
      },
      {
        id: 'revisions',
        header: 'Rev.',
        cell: ({ row }) => row.original.revisions_count ?? 0,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => <ActionsCell cert={row.original} />,
      },
    ],
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            SSL Certificates
            <Badge tone="info" appearance="subtle" size="sm">
              Beta
            </Badge>
          </span>
        }
        description="The managed certificate inventory. Issue free SSL, upload your own, and reuse across every service."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <IssueUploadCard />

      {awaitingDelegation.length > 0 && (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {awaitingDelegation.map((c) => (
            <DelegationPanel key={c.id} cert={c} />
          ))}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Inventory</CardTitle>
          <CardDescription>All certificates for your organization.</CardDescription>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div style={{ display: 'flex', gap: 'var(--vn-space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
              <Search
                size={15}
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--vn-fg-muted)' }}
              />
              <Input
                placeholder="Search by domain…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ paddingLeft: 30 }}
              />
            </div>
            <Select value={status} onValueChange={(v) => setStatus(v as CertStatus | 'all')}>
              <SelectTrigger style={{ maxWidth: 200 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 44 }} />
              ))}
            </div>
          ) : isError ? (
            <Alert
              tone="danger"
              title="Couldn't load certificates"
              actions={<Button variant="secondary" onClick={() => refetch()}>Retry</Button>}
            />
          ) : certs.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck size={20} />}
              title={search || status !== 'all' ? 'No matching certificates' : 'No certificates yet'}
              description={
                search || status !== 'all'
                  ? 'Try a different search or filter.'
                  : 'Issue a free SSL or upload your own to get started.'
              }
            />
          ) : (
            <DataTable
              columns={columns}
              data={certs}
              getRowId={(r) => r.id}
              emptyState="No certificates match your search."
              footer={`${certs.length} certificate${certs.length === 1 ? '' : 's'}${isFetching ? ' · refreshing…' : ''}`}
            />
          )}
        </CardBody>
      </Card>

      <ActivityCard />
      </div>
    </Container>
  );
}

/** Issue-free-SSL + upload-your-own, mirroring the CDN SSL tab. */
function IssueUploadCard() {
  const { show } = useToast();
  const [showUpload, setShowUpload] = useState(false);

  // issue
  const [domains, setDomains] = useState('');
  const issue = useIssueCert({
    onSuccess: (cert) => {
      trackFeatureAction('tiger', 'cert', 'create');
      if (cert.delegation) {
        show({
          tone: 'info',
          title: 'One more step — add a DNS record',
          description: 'See the DNS delegation panel below to finish issuing.',
        });
      } else {
        show({ tone: 'success', title: 'Certificate requested', description: cert.dns_names.join(', ') });
      }
      setDomains('');
    },
    onError: () => show({ tone: 'danger', title: 'Could not request certificate' }),
  });

  // upload
  const [cert, setCert] = useState('');
  const [chain, setChain] = useState('');
  const [key, setKey] = useState('');
  const importMut = useImportCert({
    onSuccess: () => {
      trackFeatureAction('tiger', 'cert', 'create');
      show({ tone: 'success', title: 'Certificate uploaded' });
      setCert('');
      setChain('');
      setKey('');
      setShowUpload(false);
    },
    onError: () => show({ tone: 'danger', title: 'Upload failed', description: 'Check the PEM blocks and key match.' }),
  });

  const parsedDomains = parseDomains(domains);
  const canIssue = parsedDomains.length > 0 && !issue.isPending;
  const canUpload =
    cert.includes('-----BEGIN CERTIFICATE-----') && key.includes('PRIVATE KEY') && !importMut.isPending;

  return (
    <Card>
      <CardHeader
        actions={
          <Button variant="secondary" startIcon={<Upload size={15} />} onClick={() => setShowUpload((s) => !s)}>
            Upload certificate
          </Button>
        }
      >
        <CardTitle>Add a certificate</CardTitle>
        <CardDescription>Issue a free managed certificate (Let's Encrypt) or upload your own.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Field
          label="Issue free SSL"
          description="Space- or comma-separated domains. A leading *. issues a wildcard (DNS-01)."
        >
          {(props) => (
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              <Input
                {...props}
                value={domains}
                placeholder="example.com  *.example.com"
                onChange={(e) => setDomains(e.target.value)}
                style={{ flex: '1 1 320px' }}
              />
              <Button
                startIcon={<ShieldCheck size={15} />}
                disabled={!canIssue}
                onClick={() => issue.mutate({ dns_names: parsedDomains })}
              >
                {issue.isPending ? 'Requesting…' : 'Issue free SSL'}
              </Button>
            </div>
          )}
        </Field>

        {showUpload && (
          <div
            style={{
              display: 'grid',
              gap: 'var(--vn-space-4)',
              borderTop: '1px solid var(--vn-border-default)',
              paddingTop: 'var(--vn-space-4)',
            }}
          >
            <Field label="Certificate (leaf + chain)" description="The server certificate; may include intermediates.">
              {(props) => (
                <Textarea
                  {...props}
                  rows={4}
                  value={cert}
                  placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----'}
                  onChange={(e) => setCert(e.target.value)}
                  style={mono}
                />
              )}
            </Field>
            <Field label="Certificate chain (optional)" description="Intermediate / root CA certs, if not already in the leaf field.">
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={chain}
                  placeholder={'-----BEGIN CERTIFICATE-----\n… Intermediate CA …\n-----END CERTIFICATE-----'}
                  onChange={(e) => setChain(e.target.value)}
                  style={mono}
                />
              )}
            </Field>
            <Field label="Private key" description="PEM with a PRIVATE KEY marker. Must match the certificate.">
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={key}
                  placeholder={'-----BEGIN PRIVATE KEY-----\n…\n-----END PRIVATE KEY-----'}
                  onChange={(e) => setKey(e.target.value)}
                  style={mono}
                />
              )}
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
              <Button variant="secondary" onClick={() => setShowUpload(false)}>
                Cancel
              </Button>
              <Button
                startIcon={<Upload size={15} />}
                disabled={!canUpload}
                onClick={() =>
                  importMut.mutate({ cert_pem: cert, key_pem: key, chain_pem: chain || undefined })
                }
              >
                {importMut.isPending ? 'Uploading…' : 'Upload certificate'}
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** A copyable label/value row (mono value + copy button with a copied tick). */
function CopyField({ label, value }: { label: string; value: string }) {
  const { show } = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      show({ tone: 'danger', title: 'Copy failed', description: 'Copy it manually.' });
    }
  };
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', fontWeight: 'var(--vn-font-weight-medium)' }}>
        {label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-2)',
          background: 'var(--vn-bg-subtle)',
          border: '1px solid var(--vn-border-default)',
          borderRadius: 'var(--vn-radius-md)',
          padding: '6px 6px 6px 10px',
        }}
      >
        <code style={{ ...mono, flex: 1, fontSize: 'var(--vn-font-size-sm)', overflowX: 'auto', whiteSpace: 'nowrap' }}>
          {value}
        </code>
        <IconButton variant="ghost" size="sm" aria-label={`Copy ${label}`} onClick={copy}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </IconButton>
      </div>
    </div>
  );
}

/**
 * External-domain (acme-dns) delegation instructions: the one-time CNAME the
 * customer adds so cert-manager can prove control and auto-renew forever.
 */
function DelegationPanel({ cert }: { cert: Cert }) {
  const { show } = useToast();
  const recheck = useRecheckCert({
    onSuccess: () =>
      show({ tone: 'info', title: 'Checking…', description: 'Verifying the DNS record and issuing shortly.' }),
    onError: () => show({ tone: 'danger', title: 'Recheck failed' }),
  });
  const d = cert.delegation!;
  return (
    <Card style={{ borderColor: 'var(--vn-border-default)' }}>
      <CardHeader>
        <CardTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            Add a DNS record to finish issuing
            <Badge tone="info" appearance="subtle" size="sm">{cert.dns_names.join(', ')}</Badge>
          </span>
        </CardTitle>
        <CardDescription>
          Add this <strong>one-time CNAME</strong> at your domain's DNS. We then validate and issue automatically —
          and renew forever with no further action.
        </CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', alignItems: 'end' }}>
          <CopyField label="Host / Name" value={d.cname_host} />
          <CopyField label="Value / Target" value={d.cname_target} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <Button
            startIcon={<RefreshCw size={15} />}
            disabled={recheck.isPending}
            onClick={() => recheck.mutate(cert.id)}
          >
            {recheck.isPending ? 'Checking…' : "I've added it — check now"}
          </Button>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            DNS can take a few minutes to propagate. This page also checks automatically.
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Download the cert material (fullchain / key / cert / chain). Fetched on demand
 * — key material is never cached (useCertBundle gcTime:0). Only shown for certs
 * whose key we hold (`reusable`) and that have issued material.
 */
function DownloadMenu({ cert }: { cert: Cert }) {
  const { show } = useToast();
  const bundle = useCertBundle(cert.id, false);
  const [busy, setBusy] = useState(false);
  const base = safeFileBase(cert.dns_names[0] ?? 'certificate');

  const grab = async (pick: (b: Bundle) => string, suffix: string) => {
    setBusy(true);
    try {
      const { data } = await bundle.refetch();
      if (!data) throw new Error('no bundle');
      const content = pick(data);
      if (!content) {
        show({ tone: 'warning', title: 'Not available', description: `This certificate has no ${suffix}.` });
        return;
      }
      triggerDownload(`${base}.${suffix}`, content);
    } catch {
      show({ tone: 'danger', title: 'Download failed', description: 'Could not fetch the certificate material.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton variant="ghost" size="sm" aria-label="Download certificate" disabled={busy}>
          <Download size={14} />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Download</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => grab((b) => b.fullchain_pem, 'fullchain.pem')}>
          Full chain (cert + intermediates)
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => grab((b) => b.key_pem, 'privkey.pem')}>
          Private key
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => grab((b) => b.cert_pem, 'cert.pem')}>
          Certificate only
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => grab((b) => b.chain_pem, 'chain.pem')}>
          Chain only
        </DropdownMenuItem>
        <DropdownMenuLabel>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', fontWeight: 'var(--vn-font-weight-regular)' }}>
            The private key is secret — store it securely.
          </span>
        </DropdownMenuLabel>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Per-row actions: download + recheck (awaiting_dns) + renew (internal LE only) + revoke. */
function ActionsCell({ cert }: { cert: Cert }) {
  const { show } = useToast();
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  const recheck = useRecheckCert({
    onSuccess: () => show({ tone: 'info', title: 'Checking…', description: 'Verifying DNS and issuing shortly.' }),
    onError: () => show({ tone: 'danger', title: 'Recheck failed' }),
  });
  const renew = useRenewCert({
    onSuccess: () => {
      trackFeatureAction('tiger', 'cert', 'edit');
      show({ tone: 'success', title: 'Renewal triggered', description: 'A new certificate is being issued.' });
    },
    onError: () => show({ tone: 'danger', title: 'Renew failed' }),
  });
  const revoke = useRevokeCert({
    onSuccess: () => {
      trackFeatureAction('tiger', 'cert', 'delete');
      show({ tone: 'success', title: 'Certificate revoked' });
      setConfirmRevoke(false);
    },
    onError: () => show({ tone: 'danger', title: 'Revoke failed' }),
  });

  const isRevoked = cert.status === 'revoked';
  const isAwaitingDNS = cert.status === 'awaiting_dns';
  // Renewable = a managed cert we can re-issue. Gate on the renewal window so we
  // don't burn CA rate limits re-issuing a cert that isn't close to expiry;
  // cert-manager auto-renews anyway. Always allow when the cert has failed.
  const days = daysUntil(cert.current_revision?.expires_at);
  const isManaged = cert.source === 'internal_le' && !isRevoked && !isAwaitingDNS;
  const inRenewWindow = cert.status === 'failed' || days === null || days <= RENEW_WINDOW_DAYS;
  const showRenew = isManaged;
  const renewBlockedReason =
    !inRenewWindow && days !== null
      ? `Renews automatically ~${RENEW_WINDOW_DAYS} days before expiry (${days} days left)`
      : null;

  const canDownload = cert.reusable && cert.status === 'active';

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        {canDownload && <DownloadMenu cert={cert} />}
        {isAwaitingDNS && (
          <Tooltip content="Check DNS now" side="top">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Recheck DNS"
              disabled={recheck.isPending}
              onClick={() => recheck.mutate(cert.id)}
            >
              <RefreshCw size={14} />
            </IconButton>
          </Tooltip>
        )}
        {showRenew && (
          <Tooltip content={renewBlockedReason ?? 'Renew now'} side="top">
            {/* span wrapper so the tooltip still shows while the button is disabled */}
            <span style={{ display: 'inline-flex' }}>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Renew certificate"
                disabled={renew.isPending || !inRenewWindow}
                onClick={() => renew.mutate(cert.id)}
              >
                <RotateCcw size={14} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {!isRevoked && (
          <Tooltip content="Revoke" side="top">
            <IconButton
              variant="ghost"
              size="sm"
              aria-label="Revoke certificate"
              onClick={() => setConfirmRevoke(true)}
            >
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        )}
      </TooltipProvider>

      {confirmRevoke && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmRevoke(false); }}
          title="Revoke certificate?"
          description={`Revoke the certificate for "${cert.dns_names[0]}"? This stops renewal and cannot be undone.`}
          confirmLabel="Revoke"
          destructive
          loading={revoke.isPending}
          onConfirm={() => revoke.mutate(cert.id)}
        />
      )}
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = {
  issue: 'Issued',
  import: 'Uploaded',
  renew: 'Renewed',
  revoke: 'Revoked',
  recheck: 'Re-checked DNS',
  download: 'Downloaded',
};

function actionLabel(a: string): string {
  return ACTION_LABEL[a] ?? a;
}

function actionTone(a: string): 'success' | 'danger' | 'info' | 'neutral' {
  if (a === 'revoke') return 'danger';
  if (a === 'issue' || a === 'import') return 'success';
  if (a === 'renew' || a === 'recheck') return 'info';
  return 'neutral';
}

/** Full local date-time for a tooltip; "" when invalid. */
function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** Human "time ago" from an ISO timestamp. */
function timeAgo(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return '';
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Tenant-scoped activity feed for the cert inventory (from auditz). */
const ACTIVITY_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All actions' },
  { value: 'issue', label: 'Issued' },
  { value: 'import', label: 'Uploaded' },
  { value: 'renew', label: 'Renewed' },
  { value: 'recheck', label: 'Re-checked DNS' },
  { value: 'download', label: 'Downloaded' },
  { value: 'revoke', label: 'Revoked' },
];

/** A key/value line in the expanded activity detail. */
function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' }}>
      <span style={{ color: 'var(--vn-fg-muted)', minWidth: 96, flexShrink: 0 }}>{label}</span>
      <span style={{ ...mono, wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

function ActivityRow({ a }: { a: Activity }) {
  const [open, setOpen] = useState(false);
  let detail: string | null = null;
  if (a.new_value && typeof a.new_value === 'object') {
    try {
      detail = JSON.stringify(a.new_value, null, 2);
    } catch {
      detail = null;
    }
  }
  return (
    <li style={{ borderBottom: '1px solid var(--vn-border-subtle, var(--vn-border-default))' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', width: '100%',
          padding: '8px 4px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
          color: 'inherit', font: 'inherit',
        }}
      >
        {open ? <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} /> : <ChevronRight size={14} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />}
        <Badge tone={actionTone(a.action)} appearance="subtle" size="sm">{actionLabel(a.action)}</Badge>
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...mono }}>
          {a.resource_name || a.resource_id}
        </span>
        <span
          style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {a.actor_email || a.actor_id || '—'}
          {a.client_ip ? <span style={{ color: 'var(--vn-fg-subtle, var(--vn-fg-muted))' }}> · {a.client_ip}</span> : null}
        </span>
        {a.status === 'failure' && <Badge tone="danger" appearance="subtle" size="sm">failed</Badge>}
        <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)', flexShrink: 0 }} title={fmtDateTime(a.occurred_at)}>
          {timeAgo(a.occurred_at)}
        </span>
      </button>
      {open && (
        <div style={{ display: 'grid', gap: 4, padding: '4px 4px 12px 32px', background: 'var(--vn-bg-subtle)' }}>
          <DetailRow label="When" value={fmtDateTime(a.occurred_at)} />
          <DetailRow label="Actor" value={a.actor_email || a.actor_id} />
          <DetailRow label="Client IP" value={a.client_ip} />
          <DetailRow label="User agent" value={a.user_agent} />
          <DetailRow label="Resource" value={`${a.resource_type} · ${a.resource_id}`} />
          {detail && (
            <div style={{ display: 'grid', gap: 4 }}>
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Details</span>
              <pre style={{ ...mono, fontSize: 'var(--vn-font-size-xs)', margin: 0, padding: 8, background: 'var(--vn-bg-surface)', border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflowX: 'auto' }}>
                {detail}
              </pre>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function ActivityCard() {
  const [action, setAction] = useState('all');
  const {
    data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useActivityInfinite({ action: action === 'all' ? undefined : action });
  const items = (data?.pages ?? []).flatMap((p) => p.items);

  return (
    <Card>
      <CardHeader
        actions={
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger style={{ minWidth: 160 }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVITY_FILTERS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      >
        <CardTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <History size={16} /> Activity
          </span>
        </CardTitle>
        <CardDescription>Who did what, when — across your organization. Click a row for details.</CardDescription>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 32 }} />)}
          </div>
        ) : isError ? (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>Activity is unavailable right now.</span>
        ) : items.length === 0 ? (
          <EmptyState icon={<History size={18} />} title="No activity yet" description="Cert actions will show up here." />
        ) : (
          <>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {items.map((a: Activity) => <ActivityRow key={a.id} a={a} />)}
            </ul>
            {hasNextPage && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--vn-space-3)' }}>
                <Button variant="secondary" size="sm" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default CertInventoryPage;
